import axios from "axios";
import { getSettings, getRequestTimeout, getUploadTimeout } from "../config";
import { logError } from "../utils/logging";

function handleUnauthorized() {
    if (window.location.pathname.includes("/login") || window.location.pathname.includes("/register")) return;

    localStorage.removeItem("token");
    localStorage.removeItem("userEmail");
    window.dispatchEvent(new Event("authChange"));
    window.location.href = "/login";
}

function createServiceClient({ baseURL, defaultAuth = true, serviceName }) {
    const client = axios.create({
        baseURL,
        timeout: getRequestTimeout(),
        // withCredentials: true, // zapni jen pokud používáš cookies
    });

    // Request interceptor: auth header + common headers + upload timeout
    client.interceptors.request.use(
        (config) => {
            const authEnabled = config.auth ?? defaultAuth; // custom field
            const token = localStorage.getItem("token");

            config.headers = config.headers ?? {};

            if (authEnabled && token) {
                config.headers.Authorization = `Bearer ${token}`;
            } else {
                // kdyby něco nastavilo Authorization dřív, tak pryč
                delete config.headers.Authorization;
            }

            // Use upload timeout for file uploads (FormData)
            // If timeout is not explicitly set, detect FormData and use upload timeout
            if (!config.timeout && (config.data instanceof FormData || config.headers['Content-Type']?.includes('multipart/form-data'))) {
                config.timeout = getUploadTimeout();
            }

            return config;
        },
        (error) => {
            logError(error, { component: "axios.request", serviceName });
            return Promise.reject(error);
        }
    );

    // Response interceptor: normalize errors + handle 401
    client.interceptors.response.use(
        (response) => response,
        (error) => {
            const status = error?.response?.status;
            const url = error?.config?.baseURL
                ? `${String(error.config.baseURL).replace(/\/+$/, "")}/${String(error.config.url || "").replace(/^\/+/, "")}`
                : error?.config?.url;

            // Handle 401 only when auth was enabled for this request
            const authEnabled = error?.config?.auth ?? defaultAuth;
            if (status === 401 && authEnabled) {
                handleUnauthorized();
            }

            // Try to extract message from FastAPI-style {detail: "..."} or {message:"..."}
            const data = error?.response?.data;
            const message =
                data?.detail ||
                data?.message ||
                error?.message ||
                (status ? `HTTP ${status}` : "Network error");

            const normalized = new Error(message);
            normalized.name = "ApiError";
            normalized.status = status ?? 0;
            normalized.url = url;
            normalized.body = data;

            logError(normalized, {
                component: "axios.response",
                serviceName,
                status: normalized.status,
                url: normalized.url,
            });

            return Promise.reject(normalized);
        }
    );

    return client;
}

export function getIdentityClient() {
    return createServiceClient({
        baseURL: "/api/identity",
        defaultAuth: false, // identityApi endpoints typicky veřejné
        serviceName: "identityApi",
    });
}

export function getSttClient() {
    return createServiceClient({
        baseURL: "/api/stt",
        defaultAuth: true, // STT typicky chráněné
        serviceName: "stt",
    });
}
